/**
 * Authentication Context
 * Manages authentication state and provides auth methods
 *
 * SECURITY: Clears React Query cache on logout to prevent data leakage between users
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { authService } from '../services';
import apiClient from '../services/api';
import type { SafeUser, LoginRequest, ApiResponse } from '../types';

interface AuthContextType {
  user: SafeUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (data: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (user: SafeUser) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<SafeUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Initialize auth state from localStorage and refresh from server
  useEffect(() => {
    const initializeAuth = async () => {
      const storedUser = authService.getCurrentUser();
      const isAuth = authService.isAuthenticated();

      if (isAuth && storedUser) {
        setUser(storedUser);

        // Fetch fresh user data from server to get latest fields (like organizationName)
        // Using apiClient ensures consistent cookie handling and interceptors
        try {
          const response = await apiClient.get<ApiResponse<{ user: SafeUser; tenant?: { id: string; name: string } }>>('/auth/me');
          if (response.data.success && response.data.data?.user) {
            // Merge fresh user data, preserving organizationName from stored user if not in response
            // This handles edge cases where the API might not return organizationName
            const freshUser = response.data.data.user;
            const organizationName = freshUser.organizationName ||
                                     response.data.data.tenant?.name ||
                                     storedUser.organizationName;

            const mergedUser = {
              ...freshUser,
              organizationName,
            };

            setUser(mergedUser);
            localStorage.setItem('user', JSON.stringify(mergedUser));
          }
        } catch (error) {
          // Silently fail - use stored user data
          console.warn('Failed to refresh user profile:', error);
        }
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const login = useCallback(
    async (data: LoginRequest) => {
      setIsLoading(true);
      try {
        // SECURITY: Clear any cached data from previous user session before login
        queryClient.clear();

        const response = await authService.login(data);
        setUser(response.user);
        setIsLoading(false);
        // Redirect super_admin to platform admin, others to dashboard
        if (response.user.role === 'super_admin') {
          navigate('/super-admin');
        } else {
          navigate('/dashboard');
        }
      } catch (error) {
        setIsLoading(false);
        throw error;
      }
    },
    [navigate, queryClient]
  );

  const logout = useCallback(async () => {
    setIsLoading(true);
    try {
      await authService.logout();
    } finally {
      // SECURITY: Clear ALL cached data to prevent data leakage to next user
      queryClient.clear();
      setUser(null);
      setIsLoading(false);
      navigate('/login');
    }
  }, [navigate, queryClient]);

  const updateUser = useCallback((updatedUser: SafeUser) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
